<template>
  <div>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submit)">
        <ShopInformation v-model="shop" />

        <div class="ShopSettings-opening-card my-8 px-10 py-3">
          <div class="ShopSettings-opening-title">{{ $t('shopSettings.openings.title') }}</div>
          <div class="ShopSettings-opening-subtitle">{{ $t('shopSettings.openings.subtitle') }}</div>
          <WeekOpenings v-model="schedules" />
        </div>

        <v-alert v-if="error" class="mt-3" color="error" data-e2e="shopEditionErrorMessage" type="error" dense>{{
          computedError
        }}</v-alert>

        <div class="mt-6 ml-auto text-right">
          <v-btn class="d-inline-block px-12" color="primary" depressed large type="submit">{{
            $t('shop.submitButton')
          }}</v-btn
          ><br />
          <v-alert
            class="ShopSettings-savedMessage px-7 mt-3"
            dense
            text
            transition="slide-y-transition"
            type="success"
            :value="saved"
            >{{ $t('shop.savedSuccess') }}</v-alert
          >
        </div>
      </form>
    </ValidationObserver>

    <div class="px-6 pt-10 pb-6 my-8 white">
      <ExceptionalClosure
        v-if="!shopExceptionalEventsLoading"
        @change="isExceptionalModified = true"
        @clickCanceled="openCanceled"
        @input="exceptions = $event"
        @removeExceptionalEvent="removeExceptionalEventLine"
        :exceptions="exceptions"
        :title="$t('shop.exceptionalClosureTitle')"
        :subtitle="$t('shop.exceptionalClosureSubtitle')"
      />
      <div v-else class="d-flex justify-center">
        <Loader />
      </div>
    </div>
    <div class="text-right">
      <v-btn
        @click="createShopExceptionalEvents"
        class="d-inline-block px-12"
        color="primary"
        depressed
        :disabled="!isExceptionalModified"
        large
      >
        {{ $t('shop.submitButton') }}
      </v-btn>
    </div>
    <AppointmentChangesModal
      @close="displayCancelAppointments = !displayCancelAppointments"
      @cancelAppointment="cancelAppointment"
      :appointments="cancelAppointments"
      :show="displayCancelAppointments"
    />
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import gql from 'graphql-tag'
import { mapGetters, mapState } from 'vuex'
import addDays from 'date-fns/addDays'

import AppointmentChangesModal from '@/components/modals/AppointmentChangesModal'
import ExceptionalClosure from '@/components/settings/ExceptionalClosure'
import Loader from '@/components/lib/Loader'
import ShopInformation from '@/components/settings/ShopInformation'
import WeekOpenings from '@/components/settings/WeekOpenings'
import { formatHour, formatHourToFloat } from '@/services/time'
import { setToBeginDay } from '@/services/date'
import CONST from '@/const/shared'

const GET_SHOP_EXCEPTIONAL_EVENTS = gql`
  query shopExceptionalEvents($limit: Int!, $offset: Int!, $shopUuid: GUID!) {
    shopExceptionalEvents(limit: $limit, offset: $offset, shopUuid: $shopUuid) {
      exceptionalEvents {
        beginDateTime
        endDateTime
        eventType
        exceptionalEventUuid
        toCancelAppointments {
          appointmentId
          beginDateTime
          customerFullName
          customerPhoneNumber
          endDateTime
          service {
            serviceType
          }
        }
      }
      total
    }
  }
`

const CREATE_SHOP_EXCEPTIONAL_EVENTS = gql`
  mutation createShopExceptionalEvents($shopUuid: GUID!, $shopExceptionalEvents: [ShopExceptionalEventInput!]!) {
    createShopExceptionalEvents(shopUuid: $shopUuid, shopExceptionalEvents: $shopExceptionalEvents) {
      beginDateTime
      endDateTime
      eventType
      exceptionalEventUuid
      toCancelAppointments {
        appointmentId
        beginDateTime
        endDateTime
        customerFullName
        customerPhoneNumber
        service {
          serviceType
        }
      }
    }
  }
`

const REMOVE_SHOP_EXCEPTIONAL_EVENT = gql`
  mutation removeShopExceptionalEvent($shopUuid: GUID!, $shopExceptionalEventUuid: GUID!) {
    removeShopExceptionalEvent(shopUuid: $shopUuid, shopExceptionalEventUuid: $shopExceptionalEventUuid)
  }
`

const CANCEL_APPOINTMENT = gql`
  mutation cancelAppointment($appointmentId: CustomId!) {
    cancelAppointment(appointmentId: $appointmentId) {
      appointmentId
    }
  }
`

const UPDATE_SHOP = gql`
  mutation updateShop($shopUuid: GUID!, $shop: UpdateShopInput!) {
    updateShop(shopUuid: $shopUuid, shop: $shop) {
      shopUuid
    }
  }
`

export default {
  name: 'ShopSettings',

  components: {
    AppointmentChangesModal,
    Loader,
    ExceptionalClosure,
    WeekOpenings,
    ShopInformation,
    ValidationObserver
  },

  data() {
    return {
      cancelAppointments: [],
      displayCancelAppointments: false,
      error: null,
      exceptions: [],
      isExceptionalModified: false,
      saved: false,
      schedules: this.getDefaultSchedules(),
      shop: {
        address1: this.$store.getters.currentShop.address1,
        address2: this.$store.getters.currentShop.address2,
        city: this.$store.getters.currentShop.city,
        displayName: this.$store.getters.currentShop.displayName,
        email: this.$store.getters.currentShop.email,
        logoLink: this.$store.getters.currentShop.logo,
        phoneNumber: this.$store.getters.currentShop.phoneNumber,
        postalCode: this.$store.getters.currentShop.postalCode
      },
      shopExceptionalEvents: [],
      shopExceptionalEventsLoading: false
    }
  },

  computed: {
    ...mapGetters(['currentShop']),
    ...mapState(['currentShopUuid']),
    computedError() {
      if (this.error.search('incompatible with service openings')) {
        return this.$t('shopSettings.errorMessage.openingsIncompatibleWithServices')
      }
      return this.error
    }
  },

  apollo: {
    shopExceptionalEvents: {
      query: GET_SHOP_EXCEPTIONAL_EVENTS,
      variables() {
        this.shopExceptionalEventsLoading = true
        return { limit: 100, offset: 0, shopUuid: this.$store.state.currentShopUuid }
      },
      result({ data }) {
        if (!data) return

        this.exceptions = data.shopExceptionalEvents.exceptionalEvents
          .map(el => this.formatExceptionalEvent(el))
          .sort((a, b) => {
            const aDate = new Date(a.beginDateTime)
            const bDate = new Date(b.beginDateTime)
            if (aDate > bDate) return -1
            else if (aDate < bDate) return 1
            return 0
          })
        this.shopExceptionalEventsLoading = false
      }
    }
  },
  mounted() {
    this.error = null
  },
  methods: {
    cancelAppointment(appointmentId) {
      this.$apollo.mutate({
        mutation: CANCEL_APPOINTMENT,
        variables: { appointmentId }
      })

      this.exceptions = this.exceptions.map(exception => {
        exception.toCancelAppointments = exception.toCancelAppointments.filter(
          appointment => appointment.appointmentId !== appointmentId
        )
        return exception
      })
      const cancelAppointmentIndex = this.cancelAppointments.findIndex(cancel => cancel.appointmentId === appointmentId)
      this.cancelAppointments.splice(cancelAppointmentIndex, 1)
    },
    async createShopExceptionalEvents() {
      const newExceptional = this.exceptions.filter(el => el.uuid).map(el => this.formatExceptionalEventToMutation(el))

      const { data } = await this.$apollo.mutate({
        mutation: CREATE_SHOP_EXCEPTIONAL_EVENTS,
        variables: { shopUuid: this.currentShopUuid, shopExceptionalEvents: newExceptional }
      })

      const editEvents = this.exceptions.filter(el => Boolean(el.uuid))
      this.isExceptionalModified = false
      this.exceptions.splice(0, editEvents.length)
      this.exceptions = [...data.createShopExceptionalEvents.map(this.formatExceptionalEvent), ...this.exceptions]
      const cancelAppointments = data.createShopExceptionalEvents
        .map(event => event.toCancelAppointments)
        .flat()
        .map(appointment => this.formatCancelAppointment(appointment))

      if (cancelAppointments.length > 0) {
        this.cancelAppointments = cancelAppointments
        this.displayCancelAppointments = true
      }
    },
    formatCancelAppointment(appointment) {
      return {
        appointmentId: appointment.appointmentId,
        endDateTime: new Date(appointment.endDateTime),
        customerFullName: appointment.customerFullName,
        customerPhoneNumber: appointment.customerPhoneNumber,
        beginDateTime: new Date(appointment.beginDateTime),
        service: { serviceType: appointment.service.serviceType }
      }
    },
    formatExceptionalEvent(event) {
      return {
        beginDateTime: new Date(event.beginDateTime),
        endDateTime: new Date(event.endDateTime),
        eventType: event.eventType,
        exceptionalEventUuid: event.exceptionalEventUuid,
        toCancelAppointments: event.toCancelAppointments,
        type: 'date'
      }
    },
    formatExceptionalEventToMutation(event) {
      return {
        beginDateTime: setToBeginDay(event.beginDateTime),
        endDateTime: setToBeginDay(addDays(event.endDateTime, 1)),
        eventType: event.eventType
      }
    },
    formatShopToMutation(shop) {
      return {
        address1: shop.address1,
        address2: shop.address2,
        city: shop.city,
        displayName: shop.displayName,
        email: shop.email,
        logo: shop.logo,
        phoneNumber: shop.phoneNumber,
        postalCode: shop.postalCode
      }
    },
    getDefaultSchedules() {
      const map = new Map()

      for (const opening of this.$store.getters.currentShop.openings) {
        const start = formatHourToFloat(opening.beginHour)
        const end = formatHourToFloat(opening.endHour)

        if (map.has(opening.dayOfWeek)) {
          const schedule = map.get(opening.dayOfWeek)

          if (start < schedule.start) {
            schedule.start = start
          }
          if (end > schedule.end) {
            schedule.end = end
          }
        } else {
          map.set(opening.dayOfWeek, {
            active: true,
            end,
            key: opening.dayOfWeek,
            maxSlot: this.$store.getters.currentShop.maxParallelAppointments[opening.dayOfWeek] || 1,
            start
          })
        }
      }

      const schedules = []

      for (let i = 1; i < 8; i++) {
        const dow = CONST.dayOfWeekByKey[i % 7]

        schedules.push(
          map.get(dow) || {
            active: false,
            end: 19,
            key: dow,
            maxSlot: 1,
            start: 8
          }
        )
      }

      return schedules
    },
    openCanceled({ exceptionalEventUuid }) {
      this.cancelAppointments = this.exceptions
        .find(event => event.exceptionalEventUuid === exceptionalEventUuid)
        .toCancelAppointments.map(event => this.formatCancelAppointment(event))
      this.displayCancelAppointments = this.cancelAppointments.length > 0
    },
    removeExceptionalEventLine(index) {
      const event = this.exceptions[index]

      if (!event.exceptionalEventUuid) {
        this.exceptions.splice(index, 1)
      } else {
        this.$store.commit('SET_CONFIRM_MODAL', {
          confirm: async () => {
            await this.removeExceptionalEvent(event)
            this.exceptions.splice(index, 1)
          },
          display: true,
          title: this.$t('shopSettings.confirmToRemoveExceptionsTitle')
        })
      }
    },
    async removeExceptionalEvent(event) {
      await this.$apollo.mutate({
        mutation: REMOVE_SHOP_EXCEPTIONAL_EVENT,
        variables: {
          shopUuid: this.currentShopUuid,
          shopExceptionalEventUuid: event.exceptionalEventUuid
        }
      })
    },
    async submit() {
      const shop = this.formatShopToMutation(this.shop)
      const openings = this.schedules
        .filter(schedule => schedule.active)
        .map(schedule => ({
          dayOfWeek: schedule.key,
          beginHour: formatHour(schedule.start).display,
          endHour: formatHour(schedule.end).display
        }))
      const maxParallelAppointments = {
        [CONST.dayOfWeek.MONDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.MONDAY).maxSlot,
        [CONST.dayOfWeek.TUESDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.TUESDAY).maxSlot,
        [CONST.dayOfWeek.WEDNESDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.WEDNESDAY).maxSlot,
        [CONST.dayOfWeek.THURSDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.THURSDAY).maxSlot,
        [CONST.dayOfWeek.FRIDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.FRIDAY).maxSlot,
        [CONST.dayOfWeek.SATURDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.SATURDAY).maxSlot,
        [CONST.dayOfWeek.SUNDAY]: this.schedules.find(day => day.key === CONST.dayOfWeek.SUNDAY).maxSlot
      }

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_SHOP,
          variables: { shopUuid: this.currentShop.shopUuid, shop: { ...shop, openings, maxParallelAppointments } }
        })
        this.error = null
        this.$store.commit('SET_SHOP', { ...this.shop, openings, maxParallelAppointments })
        this.saved = true
        setTimeout(() => (this.saved = false), 2000)
      } catch (error) {
        this.error = error.message
      }
    }
  }
}
</script>

<style scoped lang="scss">
.ShopSettings-opening-card {
  background-color: white;
}

.ShopSettings-opening-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--v-secondary-base);
}

.ShopSettings-opening-subtitle {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--v-secondary-base);
}

.ShopSettings-savedMessage {
  width: 90%;
  max-width: 150px;
  margin-left: auto;
}
</style>
